import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Search, Calendar, History, Lock, CheckCircle2, AlertCircle, Unlock } from "lucide-react";

/** FG Closing Stock — daily physical-stock entry screen.
 *  - Shows one row per active SKU from SKU Master.
 *  - Inventory Executive enters Physical Stock; Difference auto = Physical − System.
 *  - System Stock is computed dynamically: Opening + Production − Dispatch.
 *  - Same-day entries can be edited; older entries are READ-ONLY (server-enforced).
 *  - "Close Day" locks all entries; only super_admin can reopen.
 */
export default function FGClosingStock() {
  const { user, canAction } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState({ rows: [], date: today, is_today: true, is_closed: false, entered_count: 0, pending_count: 0, closed_meta: null });
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({});  // sku_id → { physical_stock, remarks }
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const isSuper = user?.role === "super_admin";
  const canEnter = canAction ? canAction("enter_closing_stock") : ["super_admin", "admin", "operations", "inventory_executive"].includes(user?.role);
  const isToday = date === today;
  const dayClosed = !!data.is_closed;
  // Read-only when: (a) historical date, OR (b) day is closed and user isn't super_admin, OR (c) user can't enter
  const readOnly = !isToday || (dayClosed && !isSuper) || !canEnter;

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/fg-closing-stock/today", { params: { date } });
      setData(d); setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load closing stock");
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return (data.rows || []).filter((r) => !search || (r.sku_id + " " + r.sku_name).toLowerCase().includes(search.toLowerCase()));
  }, [data.rows, search]);

  function setEdit(sku_id, patch) {
    setEdits((prev) => ({ ...prev, [sku_id]: { ...(prev[sku_id] || {}), ...patch } }));
  }

  function effectivePhysical(row) {
    const e = edits[row.sku_id];
    if (e && e.physical_stock !== undefined && e.physical_stock !== "") return Number(e.physical_stock);
    return row.physical_stock;
  }
  function effectiveRemarks(row) {
    const e = edits[row.sku_id];
    if (e && e.remarks !== undefined) return e.remarks;
    return row.remarks || "";
  }
  function effectiveDiff(row) {
    const p = effectivePhysical(row);
    if (p === null || p === undefined || isNaN(Number(p))) return null;
    return Number(p) - Number(row.system_stock || 0);
  }

  async function saveRow(row) {
    const e = edits[row.sku_id] || {};
    const physRaw = e.physical_stock ?? row.physical_stock;
    if (physRaw === null || physRaw === undefined || physRaw === "") {
      toast.error("Enter physical stock first");
      return;
    }
    const physical_stock = Number(physRaw);
    if (isNaN(physical_stock)) { toast.error("Physical stock must be a number"); return; }
    setBusy(true);
    try {
      await api.post("/fg-closing-stock", {
        sku_id: row.sku_id,
        physical_stock,
        remarks: e.remarks ?? row.remarks ?? "",
        date: today,
      });
      toast.success(`Saved ${row.sku_id}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  async function saveAll() {
    const dirty = Object.entries(edits).filter(([, v]) => v.physical_stock !== undefined && v.physical_stock !== "");
    if (dirty.length === 0) { toast.error("No entries to save"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const [sku_id, e] of dirty) {
      try {
        await api.post("/fg-closing-stock", {
          sku_id,
          physical_stock: Number(e.physical_stock),
          remarks: e.remarks ?? "",
          date: today,
        });
        ok++;
      } catch { fail++; }
    }
    setBusy(false);
    toast[fail ? "warning" : "success"](`Saved ${ok} entries${fail ? `, ${fail} failed` : ""}`);
    await load();
  }

  async function loadHistory(sku_id) {
    try {
      const { data: h } = await api.get("/fg-closing-stock/history", { params: { sku_id, limit: 60 } });
      setHistory(h);
      setHistoryOpen(true);
    } catch (e) { toast.error("Failed to load history"); }
  }

  async function closeDay() {
    if (!window.confirm(`Close inventory for ${today}?\n\nAfter closing, no more edits are allowed (only super_admin can reopen).`)) return;
    setBusy(true);
    try {
      await api.post("/fg-closing-stock/close-day", {});
      toast.success("Day closed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close day");
    } finally { setBusy(false); }
  }

  async function reopenDay() {
    if (!window.confirm(`Reopen ${data.date}?\n\nThis allows editing the closed day's entries.`)) return;
    setBusy(true);
    try {
      await api.post("/fg-closing-stock/reopen-day", null, { params: { date: data.date } });
      toast.success("Day reopened");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reopen");
    } finally { setBusy(false); }
  }

  const dirtyCount = Object.values(edits).filter((v) => v.physical_stock !== undefined && v.physical_stock !== "").length;
  const enteredCount = data.entered_count || 0;
  const pendingCount = data.pending_count || 0;
  const totalCount = (data.rows || []).length;

  return (
    <div className="space-y-6" data-testid="fg-closing-stock-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">FG Closing Stock</h1>
          <p className="text-sm text-slate-500 mt-1">
            System Stock = Opening + Production − Dispatch (auto-computed). Enter Physical Stock; Difference is auto-calculated.
            {isToday ? " Same-day entries can be edited until the day is closed." : " Older entries are read-only."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Completion tracker */}
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="fg-closing-tracker">
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-emerald-700">{enteredCount}</span> entered
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="font-semibold text-amber-700">{pendingCount}</span> pending
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="text-slate-500">{totalCount} total</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-slate-400" />
            <Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="fg-closing-date" />
          </div>
          {!readOnly && (
            <Button onClick={saveAll} disabled={busy || dirtyCount === 0} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="fg-closing-save-all">
              <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : (dirtyCount ? `Save Pending (${dirtyCount})` : "Save")}
            </Button>
          )}
          {/* Close Day — only on today, only when not already closed, only when something is entered */}
          {isToday && !dayClosed && canEnter && enteredCount > 0 && (
            <Button onClick={closeDay} disabled={busy} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="fg-close-day">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Close Day
            </Button>
          )}
          {/* Reopen — super_admin only, only when closed */}
          {dayClosed && isSuper && (
            <Button onClick={reopenDay} disabled={busy} variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="fg-reopen-day">
              <Unlock className="h-4 w-4 mr-1.5" /> Reopen Day
            </Button>
          )}
        </div>
      </div>

      {dayClosed && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900" data-testid="fg-day-closed-banner">
          <Lock className="h-4 w-4" /> Day {data.date} is CLOSED
          {data.closed_meta?.closed_by_name && <span className="text-emerald-700 text-xs">· closed by {data.closed_meta.closed_by_name}</span>}
          {!isSuper && <span className="ml-auto text-xs text-emerald-700">Only super_admin can reopen</span>}
        </div>
      )}

      {!isToday && !dayClosed && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900" data-testid="fg-closing-readonly-banner">
          <Lock className="h-4 w-4" /> Viewing historical data for {date}. Entries are read-only.
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by SKU ID or name…" className="pl-9" data-testid="fg-closing-search" />
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="p-3 text-left">SKU ID</th>
                <th className="p-3 text-left">SKU Name</th>
                <th className="p-3 text-right">System Stock</th>
                <th className="p-3 text-right">Physical Stock</th>
                <th className="p-3 text-right">Difference</th>
                <th className="p-3 text-left">Remarks</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-12 text-center text-slate-500">No active SKUs in SKU Master. Add some first.</td></tr>
              ) : filtered.map((r) => {
                const phys = effectivePhysical(r);
                const diff = effectiveDiff(r);
                const physRaw = edits[r.sku_id]?.physical_stock ?? (r.physical_stock !== null && r.physical_stock !== undefined ? String(r.physical_stock) : "");
                const remRaw = edits[r.sku_id]?.remarks ?? (r.remarks || "");
                const isSaved = r.physical_stock !== null && r.physical_stock !== undefined;
                const isDirty = !!edits[r.sku_id] && (edits[r.sku_id].physical_stock !== undefined || edits[r.sku_id].remarks !== undefined);
                return (
                  <tr key={r.sku_id} className={`table-row-hover ${isDirty ? "bg-amber-50/60" : ""}`} data-testid={`fg-row-${r.sku_id}`}>
                    <td className="p-3 mono text-[12px] font-semibold text-slate-900">
                      {r.sku_id}
                      {isDirty && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-500" data-testid={`fg-dirty-dot-${r.sku_id}`} title="Unsaved" />}
                    </td>
                    <td className="p-3 text-slate-900">{r.sku_name}</td>
                    <td className="p-3 text-right font-medium tabular-nums">{Number(r.system_stock || 0).toLocaleString()}</td>
                    <td className="p-3 text-right">
                      {readOnly ? (
                        <span className="tabular-nums">{phys !== null && phys !== undefined ? Number(phys).toLocaleString() : "—"}</span>
                      ) : (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={physRaw}
                          onChange={(e) => setEdit(r.sku_id, { physical_stock: e.target.value })}
                          className="w-28 text-right tabular-nums ml-auto"
                          data-testid={`fg-physical-${r.sku_id}`}
                        />
                      )}
                    </td>
                    <td className={`p-3 text-right tabular-nums font-semibold ${diff === null ? "text-slate-400" : diff < 0 ? "text-red-600" : diff > 0 ? "text-emerald-700" : "text-slate-700"}`}>
                      {diff === null ? "—" : (diff > 0 ? "+" : "") + Number(diff).toLocaleString()}
                    </td>
                    <td className="p-3">
                      {readOnly ? (
                        <span className="text-xs text-slate-500">{r.remarks || "—"}</span>
                      ) : (
                        <Input
                          value={remRaw}
                          onChange={(e) => setEdit(r.sku_id, { remarks: e.target.value })}
                          placeholder="Optional"
                          className="text-xs"
                          data-testid={`fg-remarks-${r.sku_id}`}
                        />
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end items-center">
                        {!readOnly && (
                          <Button size="sm" variant="ghost" onClick={() => saveRow(r)} disabled={busy} data-testid={`fg-save-${r.sku_id}`}>
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => loadHistory(r.sku_id)} data-testid={`fg-history-${r.sku_id}`}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                        {isSaved && <span className="text-[10px] text-emerald-700 ml-1">saved</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="p-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between">
            <span>Showing {filtered.length} of {data.rows.length} SKU(s) for {data.date}</span>
            <span className="flex items-center gap-3">
              {!readOnly && dirtyCount > 0 && <span className="text-amber-700 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {dirtyCount} unsaved</span>}
              <span>Entered <b className="text-emerald-700">{enteredCount}</b> · Pending <b className="text-amber-700">{pendingCount}</b></span>
            </span>
          </div>
        )}
      </Card>

      {historyOpen && (
        <Card className="border border-slate-200 shadow-none rounded-xl">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Closing Stock History</div>
            <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(false)}>Close</Button>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">SKU</th>
                  <th className="p-3 text-right">System</th>
                  <th className="p-3 text-right">Physical</th>
                  <th className="p-3 text-right">Diff</th>
                  <th className="p-3 text-left">Remarks</th>
                  <th className="p-3 text-left">Entered By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-500">No history yet.</td></tr>
                ) : history.map((h) => (
                  <tr key={h.id}>
                    <td className="p-3 mono text-xs">{h.date}</td>
                    <td className="p-3 mono text-xs">{h.sku_id}</td>
                    <td className="p-3 text-right tabular-nums">{Number(h.system_stock || 0).toLocaleString()}</td>
                    <td className="p-3 text-right tabular-nums">{Number(h.physical_stock || 0).toLocaleString()}</td>
                    <td className={`p-3 text-right tabular-nums ${h.difference < 0 ? "text-red-600" : h.difference > 0 ? "text-emerald-700" : "text-slate-700"}`}>{(h.difference > 0 ? "+" : "") + Number(h.difference || 0).toLocaleString()}</td>
                    <td className="p-3 text-xs text-slate-600">{h.remarks || "—"}</td>
                    <td className="p-3 text-xs text-slate-500">{h.entered_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
